import { Canvas, useFrame } from '@react-three/fiber'
import { useRef, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Html } from '@react-three/drei'
import { Users, FileText } from 'lucide-react'
import * as THREE from 'three'

const AGENT_RING_RADIUS = 4.8
const EXTERNAL_RING_OFFSET = 3.8
const INCOMING_CURVE_OFFSET_RATIO = 0.24
const OUTGOING_CURVE_OFFSET_RATIO = 0.14

// Flowing particles along a curve
function FlowingParticles({ curve, delay = 0, color = '#ffffff', speed = 3 }) {
  const pointsRef = useRef()
  
  const particles = useMemo(() => {
    const points = []
    const count = 15
    for (let i = 0; i < count; i++) {
      const t = i / count
      const point = curve.getPoint(t)
      points.push(point)
    }
    return points
  }, [curve])

  useFrame(({ clock }) => {
    if (pointsRef.current) {
      const time = clock.getElapsedTime()
      const offset = ((time * 0.3 + delay) % speed) / speed
      
      particles.forEach((_, i) => {
        const t = (i / particles.length + offset) % 1
        const point = curve.getPoint(t)
        const positions = pointsRef.current.geometry.attributes.position
        positions.setXYZ(i, point.x, point.y, point.z)
      })
      pointsRef.current.geometry.attributes.position.needsUpdate = true
    }
  })

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particles.length}
          array={new Float32Array(particles.flatMap(p => [p.x, p.y, p.z]))}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.12}
        color={color}
        transparent
        opacity={0.6}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  )
}

// Subtle glowing line
function GlowingCurve({ curve, opacity = 0.2 }) {
  return (
    <line>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={50}
          array={new Float32Array(
            Array.from({ length: 50 }, (_, i) => {
              const point = curve.getPoint(i / 49)
              return [point.x, point.y, point.z]
            }).flat()
          )}
          itemSize={3}
        />
      </bufferGeometry>
      <lineBasicMaterial
        color="#ffffff"
        transparent
        opacity={opacity}
        blending={THREE.AdditiveBlending}
      />
    </line>
  )
}

// Simple agent sphere with label and icon
function AgentSphere({ position, label }) {
  return (
    <mesh position={position}>
      <sphereGeometry args={[0.4, 16, 16]} />
      <meshStandardMaterial
        color="#666666"
        emissive="#333333"
        emissiveIntensity={0.08}
        metalness={0.5}
        roughness={0.45}
      />
      <Html center transform distanceFactor={6.5} style={{ pointerEvents: 'none' }}>
        <div className="flex flex-col items-center gap-1 bg-black/55 border border-white/15 rounded-full px-2 py-1.5 min-w-[52px]">
          <Users className="w-3.5 h-3.5 text-gray-100" strokeWidth={1.4} />
          <span className="text-[8px] uppercase tracking-[0.2em] text-gray-200">
            {label}
          </span>
        </div>
      </Html>
    </mesh>
  )
}

// Main Three.js Scene
function Scene() {
  const agents = useMemo(() => [
    { angle: 0, delay: 0, label: 'Agent 1' },
    { angle: 51.4, delay: 0.2, label: 'Agent 2' },
    { angle: 102.8, delay: 0.4, label: 'Agent 3' },
    { angle: 154.2, delay: 0.6, label: 'Agent 4' },
    { angle: 205.6, delay: 0.8, label: 'Agent 5' },
    { angle: 257, delay: 1.0, label: 'Agent 6' },
    { angle: 308.4, delay: 1.2, label: 'Agent 7' },
  ], [])

  // Calculate agent positions and curves
  const agentData = useMemo(() => {
    return agents.map((agent, index) => {
      const angle = (agent.angle * Math.PI) / 180
      const position = [
        Math.cos(angle) * AGENT_RING_RADIUS,
        Math.sin(angle) * AGENT_RING_RADIUS,
        0
      ]
      
      // Create curve from external source to agent
      const externalAngle = ((agent.angle - 25) * Math.PI) / 180
      const externalDist = AGENT_RING_RADIUS + EXTERNAL_RING_OFFSET
      const externalPos = new THREE.Vector3(
        Math.cos(externalAngle) * externalDist,
        Math.sin(externalAngle) * externalDist,
        0
      )
      
      const agentPos = new THREE.Vector3(...position)
      const midPoint = new THREE.Vector3().lerpVectors(externalPos, agentPos, 0.5)
      
      // Add perpendicular offset for curve
      const perpAngle = angle + Math.PI / 2
      const perpOffset = new THREE.Vector3(
        Math.cos(perpAngle) * (AGENT_RING_RADIUS * INCOMING_CURVE_OFFSET_RATIO),
        Math.sin(perpAngle) * (AGENT_RING_RADIUS * INCOMING_CURVE_OFFSET_RATIO),
        0
      )
      midPoint.add(perpOffset)
      
      const incomingCurve = new THREE.QuadraticBezierCurve3(
        externalPos,
        midPoint,
        agentPos
      )
      
      // Create curve from agent to center
      const centerPos = new THREE.Vector3(0, 0, 0)
      const midToCenter = new THREE.Vector3().lerpVectors(agentPos, centerPos, 0.5)
      const centerPerpAngle = angle - Math.PI / 2
      const centerPerpOffset = new THREE.Vector3(
        Math.cos(centerPerpAngle) * (AGENT_RING_RADIUS * OUTGOING_CURVE_OFFSET_RATIO),
        Math.sin(centerPerpAngle) * (AGENT_RING_RADIUS * OUTGOING_CURVE_OFFSET_RATIO),
        0
      )
      midToCenter.add(centerPerpOffset)
      
      const outgoingCurve = new THREE.QuadraticBezierCurve3(
        agentPos,
        midToCenter,
        centerPos
      )
      
      return {
        ...agent,
        position,
        incomingCurve,
        outgoingCurve,
        index
      }
    })
  }, [agents])
  
  return (
    <group>
      {/* Minimal lighting */}
      <ambientLight intensity={0.4} />
      <pointLight position={[0, 0, 8]} intensity={0.6} color="#ffffff" />
      
      {/* Agent spheres and connections */}
      {agentData.map((agent, i) => (
        <group key={i}>
          <AgentSphere position={agent.position} label={agent.label} />
          
          {/* Incoming data flow */}
          <GlowingCurve curve={agent.incomingCurve} opacity={0.15} />
          <FlowingParticles 
            curve={agent.incomingCurve} 
            delay={agent.delay} 
            color="#ffffff"
            speed={3}
          />
          
          {/* Outgoing data flow */}
          <GlowingCurve curve={agent.outgoingCurve} opacity={0.2} />
          <FlowingParticles 
            curve={agent.outgoingCurve} 
            delay={agent.delay + 1.5} 
            color="#aaaaaa"
            speed={2.5}
          />
        </group>
      ))}
    </group>
  )
}

function GameReportSkeleton() {
  const bars = [
    { width: 54, delay: 0 },
    { width: 72, delay: 0.18 },
    { width: 46, delay: 0.32 },
  ]

  return (
    <div className="flex flex-col items-center gap-1.5 mt-2">
      {bars.map((bar, index) => (
        <motion.div
          key={index}
          className="h-1.5 rounded-full bg-white/35"
          style={{
            width: `${bar.width}px`,
            transformOrigin: 'left center',
          }}
          initial={{ scaleX: 0.45, opacity: 0.55 }}
          animate={{ scaleX: [0.45, 1.05, 0.6], opacity: [0.55, 1, 0.6] }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: bar.delay,
          }}
        />
      ))}
    </div>
  )
}

// Main Component
export default function AIAgentsVisualization() {
  return (
    <div className="absolute pointer-events-none opacity-35 lg:opacity-45 right-[-40px] top-[18%] w-[240px] h-[240px] sm:right-[-24px] sm:top-[30%] sm:w-[300px] sm:h-[300px] md:right-0 md:top-1/2 md:-translate-y-1/2 md:w-[440px] md:h-[440px] lg:w-full lg:max-w-[600px] lg:h-[600px]">
      <div className="relative w-full h-full">
        {/* Three.js Canvas */}
        <Canvas
          camera={{ position: [0, 0, 14], fov: 50 }}
          style={{ background: 'transparent' }}
          gl={{ antialias: true, alpha: true }}
        >
          <Scene />
        </Canvas>
        
        <motion.div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[34%]"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <div className="flex flex-col items-center gap-1.5">
            <div className="bg-black/80 border border-white/15 rounded-full p-2.5 shadow-[0_0_15px_rgba(255,255,255,0.08)]">
              <FileText className="w-6 h-6 text-gray-400" strokeWidth={1.5} />
            </div>
            <span className="text-[7px] text-gray-500 uppercase tracking-widest font-medium">
              Game Report
            </span>
            <GameReportSkeleton />
          </div>
        </motion.div>
      </div>
    </div>
  )
}
